import { apiColor } from "@/lib/theme";
import { isDAv2 } from "@/lib/da";
import { Star, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { api, formatEURCompact, relanceLabel } from "@/lib/api";
import { toast } from "sonner";

function legacyStagePill(dossier) {
  // V3 — Statut métier strict, basé sur mandat + date_envoi_banque, pas l'étape interne
  if (!dossier) return { label: "En cours", bg: "var(--cf-border)", color: "var(--cf-neutral-text)" };
  if (dossier.status === "perdu") return { label: "Perdu", bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)" };
  if (dossier.status === "signe" || dossier.etape === "acte_authentique") {
    return { label: "Acte authentique", bg: "var(--cf-success-soft)", color: "var(--cf-success-text)" };
  }
  if (dossier.etape === "offre_editee") return { label: "Offre éditée", bg: "var(--cf-success-soft)", color: "var(--cf-success-text)" };
  if (dossier.etape === "accord_risque") return { label: "Accord risque", bg: "var(--cf-success-soft)", color: "var(--cf-success-text)" };
  if (dossier.etape === "accord_banque") return { label: "Accord banque", bg: "var(--cf-success-soft)", color: "var(--cf-success-text)" };
  if (dossier.date_envoi_banque || dossier.etape === "envoi_banque") {
    return { label: "En banque", bg: "var(--cf-primary-soft)", color: "var(--cf-primary-hover)" };
  }
  if (dossier.mandat_signe) return { label: "À envoyer", bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)" };
  return { label: "Prospect", bg: "var(--cf-bg-2)", color: "var(--cf-neutral-text)" };
}

function InlineStars({ value = 1, onChange, testid }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          onClick={(e) => { e.stopPropagation(); onChange?.(n); }}
          className="p-0.5 hover:scale-110 transition-transform"
          aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
          data-testid={`${testid}-${n}`}
        >
          <Star size={14} weight={n <= value ? "fill" : "regular"} color={n <= value ? "var(--cf-warning)" : "var(--cf-border-strong)"} />
        </button>
      ))}
    </div>
  );
}

function RelancePill({ iso }) {
  const lbl = relanceLabel(iso);
  if (!lbl) return null;
  const map = {
    red: { bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)" },
    orange: { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)" },
    blue: { bg: "var(--cf-primary-soft)", color: "var(--cf-primary-hover)" },
  };
  const p = map[lbl.tone] || map.blue;
  return (
    <span
      className="inline-flex items-center text-[11px] font-bold rounded-md px-1.5 py-0.5"
      style={{ background: p.bg, color: p.color }}
    >
      {lbl.txt}
    </span>
  );
}

/**
 * Carte dossier — NBA-first.
 * UNE seule action principale par dossier, contextuelle, exécutable en 1 clic.
 * Les autres actions restent accessibles via la fiche dossier (tap sur la ligne).
 */
export default function LegacyDossierRow({ dossier, onOpen, onChange, onAddNote }) {
  const pill = legacyStagePill(dossier);
  const nba = dossier.nba || { type: "appeler", label: "Ouvrir le dossier", color: "var(--cf-primary)", why: "" };

  const setStars = async (val) => {
    try {
      const res = await api.patch(`/dossiers/${dossier.id}`, { etoiles: val });
      onChange?.(res.data);
    } catch { toast.error("Erreur étoiles"); }
  };

  const executeNba = async (e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/dossiers/${dossier.id}/nba-execute`);
      onChange?.(res.data.dossier);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors"
      data-testid={`legacy-row-${dossier.id}`}
    >
      <div className="px-2.5 py-2 flex items-center gap-2 cursor-pointer" onClick={onOpen}>
        <span
          className="flex-shrink-0 inline-flex items-center text-[10.5px] font-bold rounded-md px-1.5 py-0.5"
          style={{ background: pill.bg, color: pill.color }}
        >
          {pill.label}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-[13px] text-slate-900 truncate">{dossier.nom}</span>
            {dossier.banques?.length > 0 && (
              <span className="text-[10.5px] text-slate-500 truncate">
                {dossier.banques.slice(0, 2).join(" / ")}
                {dossier.banques.length > 2 && " +" + (dossier.banques.length - 2)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <RelancePill iso={dossier.next_relance_date} />
            {dossier.cs_info?.chip_label && (() => {
              const sev = dossier.cs_info.severity;
              const palette = sev === "critical"
                ? { bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)" }
                : sev === "high"
                ? { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)" }
                : { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)" };
              const days = dossier.cs_info.days_to_cs;
              const txt = days < 0 ? `CS ${days}j` : `CS ${days}j`;
              return (
                <span
                  className="inline-flex items-center text-[10px] font-bold rounded-md px-1.5 py-0.5"
                  style={{ background: palette.bg, color: palette.color }}
                  data-testid={`cs-chip-${dossier.id}`}
                >
                  {txt}
                </span>
              );
            })()}
            {dossier.risk && dossier.risk.level && dossier.risk.level !== "safe" && (() => {
              const lvl = dossier.risk.level;
              const isCritique = lvl === "critique";
              const palette = isCritique
                ? { bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)" }
                : { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)" };
              const label = isCritique ? "CRITIQUE" : "RISQUE";
              return (
                <span
                  className="inline-flex items-center text-[10px] font-bold rounded-md px-1.5 py-0.5"
                  style={{ background: palette.bg, color: palette.color }}
                  title={dossier.risk.dominant_signal || ""}
                  data-testid={`risk-chip-${dossier.id}`}
                >
                  {label}
                </span>
              );
            })()}
            {dossier.mandat_signe && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-md px-1.5 py-0.5 bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)]">
                <ShieldCheck size={10} weight="fill" /> Mandat
              </span>
            )}
            {dossier.bos_delegated && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-extrabold rounded-md px-1.5 py-0.5 bg-violet-100 text-violet-800"
                title={dossier.bos_name ? `BOS — ${dossier.bos_name}` : "Délégué au BOS"}
                data-testid={`bos-badge-row-${dossier.id}`}
              >
                BOS
              </span>
            )}
            {dossier.callback_requested_at && !dossier.callback_handled_at && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-extrabold rounded-md px-1.5 py-0.5 bg-amber-100 text-amber-800"
                title="Le prospect demande à être rappelé (simulateur)"
                data-testid={`callback-badge-row-${dossier.id}`}
              >
                📞 Rappel demandé
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 text-right">
          <div className="text-[14px] font-extrabold text-[color:var(--cf-success)] leading-tight tabular-nums">
            {formatEURCompact(dossier.ca_net)}
          </div>
          <div className="mt-0.5" onClick={(e) => e.stopPropagation()}>
            <InlineStars value={dossier.etoiles || 1} onChange={setStars} testid={`stars-${dossier.id}`} />
          </div>
        </div>
      </div>

      {/* NBA — un seul CTA contextuel */}
      <button
        type="button"
        onClick={executeNba}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 font-bold text-[12.5px] transition-opacity hover:opacity-90 ${isDAv2() ? "nba-bar" : "text-white"}`}
        style={isDAv2() ? { background: `color-mix(in srgb, ${apiColor(nba.color)} 9%, white)`, color: apiColor(nba.color), borderTop: "1px solid var(--cf-border)" } : { background: apiColor(nba.color) }}
        data-testid={`nba-cta-${dossier.id}`}
      >
        <span className="flex items-center gap-1.5">
          <ArrowRight size={13} weight="bold" />
          {nba.label}
        </span>
        {nba.why && (
          <span className="text-[10.5px] font-semibold opacity-90 truncate ml-2">
            {nba.why}
          </span>
        )}
      </button>
    </div>
  );
}
